// Renders the active confirmation dialog (see lib/confirm.ts). Mounted once in
// AppShell, next to <Toaster />, so any route can call confirmDialog().
//
// Built on the shared <Modal> shell (backdrop, Escape/backdrop close, focus
// restore). It renders a custom header — a warning icon, title, and message —
// so it doesn't use Modal's standard `title` header. Unlike a per-call modal it
// carries no in-dialog "busy" state: it closes on confirm and the caller runs
// (and surfaces progress/errors for) the action, matching window.confirm()'s
// control flow.

import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';

import { Modal } from './Modal';
import { subscribeConfirm, settleConfirm, type ConfirmRequest } from '../lib/confirm';

export function ConfirmRoot() {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  // subscribeConfirm syncs the current request immediately and returns the
  // unsubscribe function — exactly what useEffect wants back.
  useEffect(() => subscribeConfirm(setRequest), []);

  if (!request) return null;

  const {
    id,
    title,
    message,
    detail,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = true,
  } = request;

  const cancel = () => settleConfirm(id, false);

  return (
    <Modal
      // A request that supersedes an open one is a new dialog: remount, so
      // its confirm button takes focus as the first one's did.
      key={id}
      onClose={cancel}
      variant="dialog"
      role={destructive ? 'alertdialog' : 'dialog'}
      labelledBy="confirm-dialog-title"
      initialFocusRef={confirmRef}
    >
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {destructive && (
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-salmon/10 text-salmon">
              <AlertTriangle size={18} />
            </div>
          )}
          <div>
            <h2 id="confirm-dialog-title" className="text-lg font-medium text-processBlue">
              {title}
            </h2>
            {message && <p className="mt-1 text-sm text-steel">{message}</p>}
          </div>
        </div>
        <button
          type="button"
          onClick={cancel}
          className="rounded-md p-1 text-steel hover:bg-cloud/30"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>

      {detail && (
        <div className="rounded-lg border border-cloud bg-cloud/20 p-3 text-sm text-carbon">
          {detail}
        </div>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={cancel}>
          {cancelLabel}
        </button>
        <button
          ref={confirmRef}
          type="button"
          // Destructive confirm uses .btn-danger (salmon); neutral confirm
          // reuses .btn-primary (OnLogic Orange). Both live in index.css's
          // @layer components alongside .btn-secondary.
          className={destructive ? 'btn-danger' : 'btn-primary'}
          onClick={() => settleConfirm(id, true)}
        >
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
